import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Repeat } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CANCEL_REASONS, DECLINE_REASONS, RIDE_TYPES, money, when } from "@/lib/rides";
import { StatusBadge, Stops } from "@/components/RideStatus";
import { ActionDialog } from "@/components/ActionDialog";
import { RideDetailsDialog } from "@/pages/admin/RideDetails";
import { Button } from "@/components/ui/button";
import { cutoffAt } from "@/lib/rider";

export function RideAdminCard({ ride }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null);
  const [schedWarn, setSchedWarn] = useState("");
  const { data: drivers } = useQuery({ queryKey: ["approvedDrivers"], queryFn: () => api.get("/admin/drivers", { params: { status: "approved" } }).then((r) => r.data), enabled: ride.status === "confirmed" });
  const call = async (path, body, msg) => {
    try {
      await api.post(`/admin/rides/${ride.id}/${path}`, body);
      toast.success(msg);
      setSchedWarn("");
      ["adminDashboard", "adminRides", "driverActive", "driverRides"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) {
      if (e.response?.data?.detail?.code === "schedule_warning") setSchedWarn(formatApiError(e));
      else toast.error(formatApiError(e), { duration: 8000 });
    }
  };
  const s = ride.status;
  return (
    <div className="space-y-3 rounded-xl border bg-card p-4" data-testid="admin-ride-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-neon-violet text-sm font-bold text-white">{ride.rider_name?.[0]}</span>
          <div>
            <p className="text-sm font-semibold" data-testid="admin-ride-rider">{ride.rider_name}</p>
            <p className="text-[11px] text-muted-foreground">Requested {when(ride.created_at)}{ride.created_by_admin ? " · by admin" : ""}</p>
            {ride.split_id && <p className="text-[11px] font-semibold text-primary" data-testid="admin-ride-split-leg">Split Trip · Leg {ride.split_leg} of 2</p>}
            {ride.scheduled_at && ["pending", "confirmed", "assigned", "paused"].includes(s) && (
              <p className="text-[11px] text-muted-foreground" data-testid="admin-ride-cutoff">
                Rider edits {cutoffAt(ride) > new Date() ? "close" : "closed"} {when(cutoffAt(ride).toISOString())}
              </p>
            )}
          </div>
        </div>
        <div className="text-right">
          <StatusBadge status={s} />
          <p className="mt-1 font-display text-xl font-bold neon-text-lime" data-testid="admin-ride-fare">{money(ride.fare)}</p>
        </div>
      </div>
      <Stops ride={ride} />
      <p className="text-xs text-muted-foreground">
        {RIDE_TYPES[ride.ride_type] || ride.ride_type} · {ride.passengers} pax · {ride.quote?.service_window_minutes ?? ride.planned_window_minutes} min window · {ride.vehicle_class?.toUpperCase()}
        {ride.series_id && <span className="ml-1 inline-flex items-center gap-1 text-neon-lime"><Repeat className="h-3 w-3" /> recurring {ride.occurrence_index}/{ride.occurrence_count}</span>}
        {ride.driver_name ? ` · Driver: ${ride.driver_name}` : ""}
      </p>
      {ride.pricing_review && <p className="text-xs font-semibold text-amber-300">PRICING REVIEW REQUIRED — edit the trip or decline</p>}
      {ride.notes && <p className="rounded-lg bg-muted px-3 py-2 text-xs">“{ride.notes}”</p>}
      {schedWarn && (
        <div className="space-y-2 rounded-lg border border-amber-400/40 bg-amber-400/10 p-3 text-xs text-amber-100" data-testid={`approve-schedule-warning-${ride.id}`}>
          <p>WARNING: {schedWarn}</p>
          <Button size="sm" variant="outline" onClick={() => call("approve", { allow_schedule_override: true }, "Ride confirmed (schedule override)")} data-testid={`approve-anyway-${ride.id}`}>Approve anyway</Button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {s === "pending" && <>
          <Button size="sm" onClick={() => call("approve", {}, "Ride confirmed")} data-testid={`approve-ride-${ride.id}`}>Approve</Button>
          <Button size="sm" variant="outline" onClick={() => setDialog("decline")} data-testid={`decline-ride-${ride.id}`}>Decline</Button>
          <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/rides/${ride.id}/edit`)} data-testid={`edit-ride-${ride.id}`}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={() => setDialog("message")} data-testid={`message-ride-${ride.id}`}>Message</Button>
        </>}
        {s === "confirmed" && <>
          <Button size="sm" onClick={() => call("assign", {}, "Assigned to you")} data-testid={`assign-me-${ride.id}`}>Assign to me</Button>
          {drivers?.length > 1 && (
            <select defaultValue="" onChange={(e) => e.target.value && call("assign", { driver_id: e.target.value }, "Driver assigned")}
              className="h-8 rounded-md border bg-background px-2 text-xs" data-testid={`assign-driver-${ride.id}`}>
              <option value="">Assign driver…</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
        </>}
        {["confirmed", "assigned", "en_route", "arrived", "in_progress"].includes(s) && (
          <Button size="sm" variant="outline" onClick={() => setDialog("cancel")} data-testid={`admin-cancel-ride-${ride.id}`}>Cancel</Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => setDialog("details")} data-testid={`admin-ride-details-${ride.id}`}>Details</Button>
      </div>
      {dialog === "decline" && <ActionDialog title="Decline request" options={DECLINE_REASONS} cta="Decline" onSubmit={(reason) => call("decline", { reason }, "Request declined")} onClose={() => setDialog(null)} />}
      {dialog === "cancel" && <ActionDialog title="Cancel ride" options={CANCEL_REASONS} cta="Cancel ride" onSubmit={(reason) => call("cancel", { reason }, "Ride cancelled")} onClose={() => setDialog(null)} />}
      {dialog === "message" && <ActionDialog title={`Message ${ride.rider_name}`} placeholder="Your message" required cta="Send" onSubmit={(text) => call("message", { text }, "Message sent")} onClose={() => setDialog(null)} />}
      {dialog === "details" && <RideDetailsDialog rideId={ride.id} onClose={() => setDialog(null)} />}
    </div>
  );
}
